import { Rating, Skeleton } from "@mui/material";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../components/Firebase/firebase";

export default function PostsShowOnHome({ amountOfPosts, category }) {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [slicedEmail, setSlicedEmail] = useState([]);
  const navigate = useNavigate();

  function manipulateDescription(postDesc) {
    if (!postDesc.data().desc) return "";
    let a = postDesc.data().desc;
    if (a.length > 100) {
      a = a.substr(0, 100) + "...";
    }
    return a;
  }

  // Handle tag click navigation
  const handleTagClick = (category, tag) => {
    navigate(
      `/explore/${encodeURIComponent(category)}/${encodeURIComponent(tag)}`
    );
  };

  // Handle category click navigation
  const handleCategoryClick = (category) => {
    navigate(`/explore/${encodeURIComponent(category)}/all`);
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const postsRef = collection(db, "posts");
        const q = query(
          postsRef,
          where("category", "==", category),
          where("privatePosts", "==", false),
          limit(amountOfPosts),
          orderBy("rating", "desc") // Changed to desc to show highest rated first
        );

        const docSnap = await getDocs(q);
        const postsData = [];
        const emailsData = [];

        docSnap.forEach((doc) => {
          postsData.push(doc);
          emailsData.push(doc.data().email);
        });

        setPosts(postsData);
        setSlicedEmail(emailsData);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [amountOfPosts, category]);

  // Loading skeleton component
  const PostSkeleton = () => (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
      <div className="animate-pulse">
        <div className="bg-gray-300 h-48 sm:h-56 w-full"></div>
        <div className="p-4 sm:p-6">
          <div className="bg-gray-300 h-6 rounded mb-3"></div>
          <div className="bg-gray-300 h-4 rounded w-3/4 mb-4"></div>
          <div className="flex justify-between mb-4">
            <div className="bg-gray-300 h-5 rounded w-16"></div>
            <div className="bg-gray-300 h-5 rounded w-20"></div>
          </div>
          <div className="flex gap-2 mb-4">
            <div className="bg-gray-300 h-6 rounded-full w-16"></div>
            <div className="bg-gray-300 h-6 rounded-full w-14"></div>
          </div>
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div className="bg-gray-300 h-6 rounded-full w-20"></div>
            <div className="bg-gray-300 h-4 rounded w-16"></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 list-none p-0 m-0">
        {loading ? (
          [...Array(amountOfPosts)].map((_, index) => (
            <li key={index}>
              <PostSkeleton />
            </li>
          ))
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <li
              key={post.id}
              className="group bg-white border border-stone-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <Link
                to={`/post/${post.data().category}/${post.id}`}
                className="block"
                reloadDocument
              >
                <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                  <img
                    src={
                      post.data().imageUrl ||
                      "https://firebasestorage.googleapis.com/v0/b/gingerrmarket.appspot.com/o/defaultImages%2Fphoto-1546069901-ba9599a7e63c.jpg?alt=media&token=c55c094f-91f0-4d7a-85d4-8ae2d125cce2"
                    }
                    alt={post.data().title}
                    loading="lazy"
                    className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              </Link>

              <div className="p-5">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-stone-500 mb-2">
                  <button
                    type="button"
                    onClick={() => handleCategoryClick(post.data().category)}
                    className="font-medium text-emerald-800 hover:text-emerald-900"
                  >
                    {(post.data().category || "").replace(/-/g, " ")}
                  </button>
                  <span aria-hidden>·</span>
                  <span>{post.data().calorieTotal || 0} cal</span>
                </div>

                <h3
                  className="text-xl font-semibold text-stone-900 leading-snug"
                  style={{ fontFamily: "Fraunces, Georgia, serif" }}
                >
                  <Link
                    reloadDocument
                    to={`/post/${post.data().category}/${post.id}`}
                    className="hover:underline"
                  >
                    {post.data().title}
                  </Link>
                </h3>

                {post.data().desc && (
                  <p className="mt-2 text-sm text-stone-600 line-clamp-2">
                    {manipulateDescription(post)}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between pt-4 border-t border-stone-100">
                  <Rating
                    name="read-only"
                    value={post.data().rating || 0}
                    size="small"
                    precision={0.5}
                    readOnly
                  />
                  <Link
                    reloadDocument
                    to={`/profile/${post.data().username_of_user}`}
                    className="text-xs text-stone-500 hover:text-stone-700"
                  >
                    by {post.data().username_of_user || "anonymous"}
                  </Link>
                </div>
              </div>
            </li>
          ))
        ) : (
          <li className="col-span-full text-center py-16 border border-dashed border-stone-300 rounded-lg bg-white">
            <h3 className="text-lg font-semibold text-stone-700">
              No {category.replace(/-/g, " ")} recipes yet
            </h3>
            <p className="mt-2 text-stone-500">
              Be the first to share a{" "}
              {category.replace(/-/g, " ").toLowerCase()} recipe!
            </p>
          </li>
        )}
      </ul>
    </div>
  );
}
